import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { useState, type ReactElement } from "react";
import { ConfirmDialog, ContentHeader, Field, Modal, MoneyInput, QueryStatus, useToast } from "@cx/ui";
import type { CommissionEntryView, CommissionPaymentOptions, CommissionStatement } from "@cx/contracts";
import { addDays, formatBRL, formatDateBR, todayInSaoPaulo } from "@cx/domain";
import { can, type Permissions } from "@cx/db";
import { ApiFailure } from "../../api";
import { errorMessage } from "../../errors";
import { useSession } from "../../session";
import { COMMISSION_BASE_LABEL } from "../sellers/labels";
import {
  canReverseEntry,
  entryLabel,
  money,
  monthName,
  monthPeriod,
  reversePath,
  statementPath,
} from "./labels";

/** Extrato da conta-corrente do vendedor no mês (RF-VEN-08): resumo, lançamentos, pendentes e ações. */
export function CommissionStatementPage(): ReactElement {
  const { sellerId } = useParams({ from: "/vendas/comissoes/$sellerId" });
  const search = useSearch({ from: "/vendas/comissoes/$sellerId" });
  const navigate = useNavigate({ from: "/vendas/comissoes/$sellerId" });
  const { companyId, me, api } = useSession();
  const qc = useQueryClient();
  const toast = useToast();
  const perms = me.permissions as Permissions;
  const canEntry = can(perms, "commissions", "create");
  const canPay = can(perms, "commissions", "pay") && can(perms, "payables", "create");
  const canReversePay = can(perms, "commissions", "pay") && can(perms, "payables", "update");
  const canSeePayables = can(perms, "payables", "read");
  const month = search.month ?? todayInSaoPaulo().slice(0, 7);
  const period = monthPeriod(month);
  const [dialog, setDialog] = useState<"entry" | "payment" | null>(null);
  const [reversing, setReversing] = useState<CommissionEntryView | null>(null);
  const statement = useInfiniteQuery({
    queryKey: ["commissions", companyId, "statement", sellerId, month],
    initialPageParam: "",
    queryFn: ({ pageParam }) =>
      api<CommissionStatement>(
        statementPath(sellerId, {
          ...(period ?? {}),
          ...(pageParam ? { cursor: pageParam } : {}),
        }),
      ),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const first = statement.data?.pages[0];
  const rows = statement.data?.pages.flatMap((p) => p.data) ?? [];
  const changed = () => qc.invalidateQueries({ queryKey: ["commissions", companyId] });
  const reverse = useMutation({
    mutationFn: (e: CommissionEntryView) =>
      api(reversePath(e), {
        method: "POST",
        headers: { "Idempotency-Key": crypto.randomUUID() },
        body: JSON.stringify({}),
      }),
    onSuccess: () => {
      toast("success", "Lançamento estornado");
      changed();
      qc.invalidateQueries({ queryKey: ["titles", companyId] });
    },
    onError: (e) => toast("danger", errorMessage(e)),
  });
  const reversible = (e: CommissionEntryView) =>
    canReverseEntry(e) && (e.kind === "payment" ? canReversePay : canEntry);
  const s = first?.summary;
  const name = first?.seller.name ?? "Vendedor";

  return (
    <>
      <ContentHeader
        title={`Comissões de ${name}`}
        breadcrumb={["Vendas", "Comissões", name]}
        actions={
          <>
            {canEntry && (
              <button
                type="button"
                className="btn btn-outline-primary me-2"
                onClick={() => setDialog("entry")}
              >
                Lançar crédito/débito
              </button>
            )}
            {canPay && (
              <button type="button" className="btn btn-primary" onClick={() => setDialog("payment")}>
                Lançar pagamento
              </button>
            )}
          </>
        }
      />
      <div className="row g-2 mb-3 align-items-end">
        <div className="col-7 col-md-3">
          <label className="form-label small" htmlFor="st-month">
            Mês
          </label>
          <input
            id="st-month"
            type="month"
            className="form-control"
            value={month}
            onChange={(e) => navigate({ search: { month: e.target.value || undefined }, replace: true })}
          />
        </div>
        <div className="col-12 col-md-9">
          <Link to="/vendas/comissoes" search={{ month }} className="small">
            Voltar ao resumo de {monthName(month)}
          </Link>
        </div>
      </div>
      <QueryStatus loading={statement.isLoading} error={statement.error} />
      {s && first && (
        <div className="box card mb-3">
          <div className="table-responsive">
            <table className="table mb-0" aria-label="Resumo do mês">
              <tbody>
                <SummaryRow label="Base de comissão" value={COMMISSION_BASE_LABEL[first.seller.base]} />
                <SummaryRow label="Faturado" value={money(s.invoicedCents)} />
                <SummaryRow label="Saldo anterior" value={money(s.openingCents)} />
                <SummaryRow label="(+) Comissões do mês" value={money(s.commissionsCents)} />
                <SummaryRow label="(+) Outros créditos" value={money(s.creditsCents)} />
                <SummaryRow label="(−) Outros débitos" value={money(s.debitsCents)} />
                <SummaryRow label="(−) Pagamentos" value={money(s.paymentsCents)} />
                <SummaryRow label="(*) Comissões pendentes" value={money(s.pendingCents)} muted />
                <SummaryRow
                  label="(=) Saldo"
                  value={money(s.closingCents)}
                  strong
                  testId="statement-closing"
                />
                <SummaryRow label="Saldo atual" value={money(s.balanceCents)} testId="statement-balance" />
              </tbody>
            </table>
          </div>
        </div>
      )}
      <div className="box card mb-3">
        <h2 className="h6 px-3 pt-3 pb-2 mb-0">Lançamentos</h2>
        {statement.isSuccess && rows.length === 0 && (
          <p className="text-muted px-3 pb-3 mb-0">Nenhum lançamento no mês.</p>
        )}
        {rows.length > 0 && (
          <div className="table-responsive">
            <table className="table align-middle mb-0" aria-label="Lançamentos de comissão">
              <thead>
                <tr>
                  <th scope="col">Data</th>
                  <th scope="col">Histórico</th>
                  <th scope="col" className="text-end">
                    Base
                  </th>
                  <th scope="col" className="text-end">
                    Valor
                  </th>
                  <th scope="col">
                    <span className="visually-hidden">Ações</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((e) => (
                  <tr key={e.id} className={e.reversedAt ? "text-muted" : undefined}>
                    <td>{formatDateBR(e.date)}</td>
                    <td>
                      {entryLabel(e)}
                      {e.reversedAt && <span className="badge text-bg-secondary ms-2">Estornado</span>}
                      {e.orderId && (
                        <span className="small ms-2">
                          (
                          <Link to="/vendas/pedidos/$id" params={{ id: e.orderId }}>
                            ver pedido
                          </Link>
                          )
                        </span>
                      )}
                      {e.titleId && e.kind === "payment" && canSeePayables && (
                        <span className="small ms-2">
                          (
                          <Link to="/financas/contas-a-pagar/$id" params={{ id: e.titleId }}>
                            ver conta a pagar
                          </Link>
                          )
                        </span>
                      )}
                    </td>
                    <td className="text-end">{e.baseCents === null ? "—" : formatBRL(e.baseCents)}</td>
                    <td className={`text-end ${e.amountCents < 0 ? "text-danger" : "text-success"}`}>
                      {formatBRL(e.amountCents)}
                    </td>
                    <td className="text-end">
                      {reversible(e) && (
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          aria-label={`Estornar ${entryLabel(e)} de ${formatDateBR(e.date)}`}
                          disabled={reverse.isPending}
                          onClick={() => setReversing(e)}
                        >
                          Estornar
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {statement.hasNextPage && (
          <div className="p-3 text-center">
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              disabled={statement.isFetchingNextPage}
              onClick={() => statement.fetchNextPage()}
            >
              Carregar mais
            </button>
          </div>
        )}
      </div>
      {first && first.pending.length > 0 && (
        <div className="box card">
          <h2 className="h6 px-3 pt-3 pb-2 mb-0">Comissões pendentes (a liberar no recebimento)</h2>
          <div className="table-responsive">
            <table className="table align-middle mb-0" aria-label="Comissões pendentes">
              <thead>
                <tr>
                  <th scope="col">Pedido</th>
                  <th scope="col">Contas lançadas em</th>
                  <th scope="col" className="text-end">
                    Comissão
                  </th>
                  <th scope="col" className="text-end">
                    Liberada
                  </th>
                  <th scope="col" className="text-end">
                    Pendente
                  </th>
                </tr>
              </thead>
              <tbody>
                {first.pending.map((p) => (
                  <tr key={p.orderId}>
                    <td>
                      {p.orderNumber === null ? (
                        "—"
                      ) : (
                        <Link to="/vendas/pedidos/$id" params={{ id: p.orderId }}>
                          Pedido de venda nº {p.orderNumber}
                        </Link>
                      )}
                    </td>
                    <td>{formatDateBR(p.postedOn)}</td>
                    <td className="text-end">{formatBRL(p.commissionCents)}</td>
                    <td className="text-end">{formatBRL(p.releasedCents)}</td>
                    <td className="text-end">{formatBRL(p.pendingCents)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {dialog === "entry" && (
        <EntryDialog sellerId={sellerId} onClose={() => setDialog(null)} onSaved={changed} />
      )}
      {dialog === "payment" && (
        <PaymentDialog
          sellerId={sellerId}
          balanceCents={s?.balanceCents ?? null}
          onClose={() => setDialog(null)}
          onSaved={() => {
            changed();
            qc.invalidateQueries({ queryKey: ["titles", companyId] });
          }}
        />
      )}
      <ConfirmDialog
        open={!!reversing}
        title="Estornar lançamento"
        message={
          reversing?.kind === "payment"
            ? "Estornar o pagamento? A conta a pagar gerada é cancelada (se tiver baixa, estorne a baixa antes) e um lançamento inverso com a data de hoje volta ao saldo."
            : "Estornar o lançamento? Um lançamento inverso com a data de hoje é incluído; o original fica no extrato."
        }
        confirmLabel="Estornar"
        onConfirm={() => {
          if (reversing) reverse.mutate(reversing);
          setReversing(null);
        }}
        onCancel={() => setReversing(null)}
      />
    </>
  );
}

function SummaryRow({
  label,
  value,
  strong,
  muted,
  testId,
}: {
  label: string;
  value: string;
  strong?: boolean;
  muted?: boolean;
  testId?: string;
}) {
  return (
    <tr className={muted ? "text-muted" : undefined}>
      <th scope="row" className={strong ? "fw-semibold" : "fw-normal"}>
        {label}
      </th>
      <td className={`text-end${strong ? " fw-semibold" : ""}`} data-testid={testId}>
        {value}
      </td>
    </tr>
  );
}

/** Erros de campo do servidor; os de regra mais comuns vão para o campo certo. */
function fieldErrors(e: unknown): Record<string, string> {
  if (!(e instanceof ApiFailure)) return {};
  if (e.fields) return e.fields;
  if (e.code === "future_date") return { date: errorMessage(e) };
  if (e.code === "amount_exceeds_balance") return { amountCents: errorMessage(e) };
  return {};
}

function EntryDialog({ sellerId, onClose, onSaved }: { sellerId: string; onClose(): void; onSaved(): void }) {
  const { api } = useSession();
  const toast = useToast();
  const [open, setOpen] = useState(true);
  const [errors, setErrors] = useState<Record<string, string>>({});
  // Uma chave por formulário aberto: reenviar o mesmo formulário não duplica o lançamento.
  const [key] = useState(() => crypto.randomUUID());
  const [v, setV] = useState({
    kind: "credit" as "credit" | "debit",
    date: todayInSaoPaulo(),
    amountCents: null as number | null,
    notes: "",
  });
  const save = useMutation({
    mutationFn: () =>
      api("/commissions/entries", {
        method: "POST",
        headers: { "Idempotency-Key": key },
        body: JSON.stringify({ ...v, sellerId, amountCents: v.amountCents ?? 0 }),
      }),
    onSuccess: () => {
      toast("success", "Lançamento incluído");
      onSaved();
      setOpen(false);
    },
    onError: (e) => {
      setErrors(fieldErrors(e));
      toast("danger", errorMessage(e));
    },
  });
  return (
    <Modal
      open={open}
      title="Lançar crédito ou débito"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-outline-secondary" onClick={() => setOpen(false)}>
            Cancelar
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={save.isPending}
            onClick={() => save.mutate()}
          >
            Salvar
          </button>
        </>
      }
    >
      <fieldset className="mb-3">
        <legend className="form-label fs-6">Tipo</legend>
        {(["credit", "debit"] as const).map((k) => (
          <div key={k} className="form-check form-check-inline">
            <input
              id={`com-entry-${k}`}
              type="radio"
              name="com-entry-kind"
              className="form-check-input"
              checked={v.kind === k}
              onChange={() => setV({ ...v, kind: k })}
            />
            <label className="form-check-label" htmlFor={`com-entry-${k}`}>
              {k === "credit" ? "Outro crédito" : "Outro débito"}
            </label>
          </div>
        ))}
      </fieldset>
      <div className="row">
        <Field label="Data" required className="col-md-6" error={errors.date}>
          <input
            type="date"
            value={v.date}
            max={todayInSaoPaulo()}
            onChange={(e) => setV({ ...v, date: e.target.value })}
          />
        </Field>
        <Field label="Valor" required className="col-md-6" error={errors.amountCents}>
          <MoneyInput value={v.amountCents} data-autofocus onChange={(c) => setV({ ...v, amountCents: c })} />
        </Field>
        <Field label="Motivo" required className="col-12" error={errors.notes}>
          <input value={v.notes} maxLength={500} onChange={(e) => setV({ ...v, notes: e.target.value })} />
        </Field>
      </div>
    </Modal>
  );
}

function PaymentDialog({
  sellerId,
  balanceCents,
  onClose,
  onSaved,
}: {
  sellerId: string;
  balanceCents: number | null;
  onClose(): void;
  onSaved(): void;
}) {
  const { companyId, api } = useSession();
  const toast = useToast();
  const [open, setOpen] = useState(true);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [key] = useState(() => crypto.randomUUID());
  const today = todayInSaoPaulo();
  const [v, setV] = useState({
    amountCents: balanceCents !== null && balanceCents > 0 ? balanceCents : (null as number | null),
    date: today,
    dueDate: addDays(today, 0),
    financialCategoryId: null as string | null,
    paymentMethodId: null as string | null,
    notes: "",
  });
  const options = useQuery({
    queryKey: ["commissions", companyId, "payment-options"],
    queryFn: () => api<CommissionPaymentOptions>("/commissions/payment-options"),
  });
  const save = useMutation({
    mutationFn: () =>
      api("/commissions/payments", {
        method: "POST",
        headers: { "Idempotency-Key": key },
        body: JSON.stringify({ ...v, sellerId, amountCents: v.amountCents ?? 0, notes: v.notes || null }),
      }),
    onSuccess: () => {
      toast("success", "Pagamento lançado e conta a pagar gerada");
      onSaved();
      setOpen(false);
    },
    onError: (e) => {
      setErrors(fieldErrors(e));
      toast("danger", errorMessage(e));
    },
  });
  return (
    <Modal
      open={open}
      title="Lançar pagamento de comissões"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-outline-secondary" onClick={() => setOpen(false)}>
            Cancelar
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={save.isPending}
            onClick={() => save.mutate()}
          >
            Lançar pagamento
          </button>
        </>
      }
    >
      <p className="text-muted small">
        O pagamento sai do saldo do vendedor ({money(balanceCents)}) e gera uma conta a pagar para ele em
        Finanças.
      </p>
      <div className="row">
        <Field label="Valor" required className="col-md-6" error={errors.amountCents}>
          <MoneyInput value={v.amountCents} data-autofocus onChange={(c) => setV({ ...v, amountCents: c })} />
        </Field>
        <Field label="Data do pagamento" required className="col-md-6" error={errors.date}>
          <input
            type="date"
            value={v.date}
            max={today}
            onChange={(e) => setV({ ...v, date: e.target.value })}
          />
        </Field>
        <Field label="Vencimento da conta a pagar" required className="col-md-6" error={errors.dueDate}>
          <input type="date" value={v.dueDate} onChange={(e) => setV({ ...v, dueDate: e.target.value })} />
        </Field>
        <Field label="Categoria" className="col-md-6" error={errors.financialCategoryId}>
          <select
            className="form-select"
            value={v.financialCategoryId ?? ""}
            onChange={(e) => setV({ ...v, financialCategoryId: e.target.value || null })}
          >
            <option value="">Sem categoria</option>
            {options.data?.categories
              .filter((c) => c.active)
              .map((c) => (
                <option key={c.id} value={c.id}>
                  {c.path}
                </option>
              ))}
          </select>
        </Field>
        <Field label="Forma de pagamento" className="col-md-6" error={errors.paymentMethodId}>
          <select
            className="form-select"
            value={v.paymentMethodId ?? ""}
            onChange={(e) => setV({ ...v, paymentMethodId: e.target.value || null })}
          >
            <option value="">Não informada</option>
            {options.data?.paymentMethods
              .filter((m) => m.active)
              .map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                </option>
              ))}
          </select>
        </Field>
        <Field label="Observações" className="col-md-6" error={errors.notes}>
          <input value={v.notes} maxLength={500} onChange={(e) => setV({ ...v, notes: e.target.value })} />
        </Field>
      </div>
    </Modal>
  );
}
